import { useMemo } from 'react';

import _ from 'lodash';

import { getConfigInfo } from 'utils/loanManagement';

import { getTitleCompanyFormConfig } from '../../EditDrawer.companyConfig';
import {
  getClosingFundingDatesFormConfig,
  getFundingInformationFormConfig,
  getSchedulingFormConfig,
  getTitleHolderFormConfig,
} from '../../EditDrawer.config';
import { getManufacturedHomeContactsFormConfig } from '../../EditDrawer.manufacturedHomeContactsConfig';
import { ALL_HIDDEN_FORM_FIELDS } from '../Content.constants';
import {
  buildBrokerApplicationFields,
  buildDealerApplicationFields,
  buildInvestorApplicationFields,
} from '../Content.manufacturedHomeContacts.utils';
import {
  buildAttorneyApplicationFields,
  buildCompanyApplicationFields,
  buildFundingApplicationData,
  buildTitleHolderApplicationData,
} from '../Content.utils';

export const useFullConfig = ({
  isTwoPartClosing,
  isUnsecured,
  earliestClosingDate,
  currentCloseAt,
  attendantCompanyOptions,
  titleCompanyOptions,
  escrowCompanyOptions,
  onTitleCompanyChange,
  onEscrowCompanyChange,
  onChangeTitleLocation,
  onChangeEscrowLocation,
  hasTitleCompany,
  hasEscrowCompany,
  sellerAttorneyOptions,
  buyerAttorneyOptions,
  onSellerAttorneyChange,
  onBuyerAttorneyChange,
  hasSellerAttorney,
  hasBuyerAttorney,
  currentNoteDate,
  businessDays,
  warehouseBankOptions,
  onFeeProviderChange,
  onOpenFeeProviderLocationTable,
  isChangeLocationButtonDisabled,
  isChangeLocationButtonVisible,
  onChangeAdvanceAmountPercentDependantField,
  borrowerVestingOptions,
  filteredMannerHeldOptions,
  isTrustSectionVisible,
  isBplLoanPurposeType,
  closingInfo,
  fundingInfo,
  trustInfo,
  titleCompany,
  escrowCompany,
  sellerAttorney,
  buyerAttorney,
  isRescissionApplicable,
  isChattel,
  chattelFundingDate,
  brokerOptions,
  dealerOptions,
  investorOptions,
  onBrokerChange,
  onDealerChange,
  onInvestorChange,
  hasBroker,
  hasDealer,
  hasInvestor,
  brokerCompany,
  dealerCompany,
  investorCompany,
  chattelDetails,
}) => {
  const schedulingFullConfig = useMemo(
    () =>
      getSchedulingFormConfig({
        isTwoPartClosing,
        isUnsecured,
        earliestClosingDate,
        closeAt: currentCloseAt,
      }),
    [isTwoPartClosing, isUnsecured, earliestClosingDate, currentCloseAt],
  );

  const titleCompanyFullConfig = useMemo(
    () =>
      getTitleCompanyFormConfig({
        attendantCompanyOptions,
        titleCompanyOptions,
        escrowCompanyOptions,
        onTitleCompanyChange,
        onEscrowCompanyChange,
        onChangeTitleLocation,
        onChangeEscrowLocation,
        hasTitleCompany,
        hasEscrowCompany,
        sellerAttorneyOptions,
        buyerAttorneyOptions,
        onSellerAttorneyChange,
        onBuyerAttorneyChange,
        hasSellerAttorney,
        hasBuyerAttorney,
      }),
    [
      attendantCompanyOptions,
      titleCompanyOptions,
      escrowCompanyOptions,
      onTitleCompanyChange,
      onEscrowCompanyChange,
      onChangeTitleLocation,
      onChangeEscrowLocation,
      hasTitleCompany,
      hasEscrowCompany,
      sellerAttorneyOptions,
      buyerAttorneyOptions,
      onSellerAttorneyChange,
      onBuyerAttorneyChange,
      hasSellerAttorney,
      hasBuyerAttorney,
    ],
  );

  const closingDatesFullConfig = useMemo(
    () =>
      getClosingFundingDatesFormConfig({
        hasCloseAt: !!currentCloseAt,
        hasNoteDate: !!currentNoteDate,
        isUnsecured,
        businessDays,
        isRescissionApplicable,
        isChattel,
        fundingDate: chattelFundingDate,
      }),
    [
      currentCloseAt,
      currentNoteDate,
      isUnsecured,
      businessDays,
      isRescissionApplicable,
      isChattel,
      chattelFundingDate,
    ],
  );

  const fundingFullConfig = useMemo(
    () =>
      getFundingInformationFormConfig({
        warehouseBankOptions,
        feeProviderOptions: attendantCompanyOptions,
        onFeeProviderChange,
        onOpenFeeProviderLocationTable,
        isChangeLocationButtonDisabled,
        isChangeLocationButtonVisible,
        onChangeAdvanceAmountPercentDependantField,
        isUnsecured,
      }),
    [
      warehouseBankOptions,
      attendantCompanyOptions,
      onFeeProviderChange,
      onOpenFeeProviderLocationTable,
      isChangeLocationButtonDisabled,
      isChangeLocationButtonVisible,
      onChangeAdvanceAmountPercentDependantField,
      isUnsecured,
    ],
  );

  const titleHolderFullConfig = useMemo(
    () =>
      getTitleHolderFormConfig({
        borrowerVestingOptions,
        mannerHeldOptions: filteredMannerHeldOptions,
        isTrustSectionVisible,
        isMannerHeldDisabled: isTrustSectionVisible,
        isCompanyNameFieldVisible: true,
        isBplLoanPurposeType,
        isChattel,
      }),
    [
      borrowerVestingOptions,
      filteredMannerHeldOptions,
      isTrustSectionVisible,
      isBplLoanPurposeType,
      isChattel,
    ],
  );

  const manufacturedHomeContactsFullConfig = useMemo(
    () =>
      getManufacturedHomeContactsFormConfig({
        brokerOptions,
        dealerOptions,
        investorOptions,
        onBrokerChange,
        onDealerChange,
        onInvestorChange,
        hasBroker,
        hasDealer,
        hasInvestor,
      }),
    [
      brokerOptions,
      dealerOptions,
      investorOptions,
      onBrokerChange,
      onDealerChange,
      onInvestorChange,
      hasBroker,
      hasDealer,
      hasInvestor,
    ],
  );

  const fullConfig = useMemo(
    () => ({
      groups: [
        ...schedulingFullConfig.groups,
        ...titleCompanyFullConfig.groups,
        ...closingDatesFullConfig.groups,
        ...fundingFullConfig.groups,
        ...manufacturedHomeContactsFullConfig.groups,
        ...titleHolderFullConfig.groups,
      ],
    }),
    [
      schedulingFullConfig,
      titleCompanyFullConfig,
      closingDatesFullConfig,
      fundingFullConfig,
      manufacturedHomeContactsFullConfig,
      titleHolderFullConfig,
    ],
  );

  const applicationData = useMemo(
    () => ({
      ...(closingInfo || {}),
      ...buildCompanyApplicationFields(titleCompany, 'title'),
      ...buildCompanyApplicationFields(escrowCompany, 'escrow'),
      ...buildAttorneyApplicationFields(sellerAttorney, 'sellerAttorney'),
      ...buildAttorneyApplicationFields(buyerAttorney, 'buyerAttorney'),
      ...buildBrokerApplicationFields(brokerCompany),
      ...buildDealerApplicationFields(dealerCompany, chattelDetails?.contractDate),
      ...buildInvestorApplicationFields(investorCompany),
      ...closingInfo?.prepaidInterestCreditData,
      approvalDate: closingInfo?.approvalDate,
      approvalExpirationDate: closingInfo?.approvalExpirationDate,
      maturityDate: closingInfo?.maturityDate,
      ...(fundingInfo ? buildFundingApplicationData(fundingInfo) : {}),
      ...buildTitleHolderApplicationData({ closingInfo, trustInfo }),
    }),
    [
      closingInfo,
      fundingInfo,
      trustInfo,
      titleCompany,
      escrowCompany,
      sellerAttorney,
      buyerAttorney,
      brokerCompany,
      dealerCompany,
      investorCompany,
      chattelDetails,
    ],
  );

  const {
    initialFormValues: configFormValues,
    fieldsConfig,
    observableFieldsNames,
    schema,
  } = useMemo(
    () => getConfigInfo({ applicationData, config: fullConfig }),
    [applicationData, fullConfig],
  );

  const initialFormValues = useMemo(
    () => ({
      ...configFormValues,
      ..._.pick(applicationData, ALL_HIDDEN_FORM_FIELDS),
    }),
    [configFormValues, applicationData],
  );

  return {
    fullConfig,
    applicationData,
    initialFormValues,
    fieldsConfig,
    observableFieldsNames,
    schema,
  };
};
